/**
 * NotesPanelContent Component
 *
 * Notes side panel: wires the note store/filters into the shared
 * `ArtifactListPanelContent` shell and supplies the note list.
 */

import { memo, useCallback, useEffect } from 'react';
import { IconNotes } from '@tabler/icons-react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '@/store';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useTabAwareArtifactNavigation } from '@/hooks/useTabAwareArtifactNavigation';
import { selectCurrentWorkspaceId } from '@/store/selectors/authSelectors';
import {
  selectNotes,
  selectNotesIsLoading,
  selectNotesHasLoaded,
  selectNotesSelectedId,
  selectNotesHasNextPage,
  selectNotesTotalCount,
  selectHasActiveNoteFiltersInMapPanel,
  selectNoteFilterKey,
} from '@/store/selectors/noteSelectors';
import { NoteCard } from '@/components/notes/NoteCard';
import { NoteFilters, NoteFilterPills } from '@/components/notes/NoteFilters';
import { PaginatedList } from '@/components/ui/PaginatedList';
import { EmptyState } from '@/components/common/EmptyState';
import { ICON_SIZE_EMPTY_STATE } from '@/constants/ui';
import type { NoteListItem } from '@/types/notes';
import { ArtifactListPanelContent } from './ArtifactListPanelContent';
import styles from './SidePanel.module.css';

interface NotesPanelContentProps {
  readonly onClose: () => void;
}

export const NotesPanelContent = memo(function NotesPanelContent({
  onClose,
}: NotesPanelContentProps) {
  const { t } = useTranslation();

  // Atomic state selectors
  const notes = useGeoMarkupStore(useShallow(selectNotes));
  const isLoading = useGeoMarkupStore(selectNotesIsLoading);
  const hasLoaded = useGeoMarkupStore(selectNotesHasLoaded);
  const selectedId = useGeoMarkupStore(selectNotesSelectedId);
  const hasNextPage = useGeoMarkupStore(selectNotesHasNextPage);
  const totalCount = useGeoMarkupStore(selectNotesTotalCount);
  const hasActiveFilters = useGeoMarkupStore(
    selectHasActiveNoteFiltersInMapPanel
  );
  const filterKey = useGeoMarkupStore(selectNoteFilterKey);

  const currentWorkspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);

  // Action selectors (stable references, no re-renders)
  const fetchNotes = useGeoMarkupStore((state) => state.notes.fetchNotes);
  const loadMoreNotes = useGeoMarkupStore((state) => state.notes.loadMoreNotes);
  const setSearch = useGeoMarkupStore((state) => state.noteFilters.setSearch);
  const searchValue = useGeoMarkupStore((state) => state.noteFilters.search);
  const { openArtifact } = useArtifactNavigation();
  const { navigateToNoteFromList } = useTabAwareArtifactNavigation();
  const isNoteFormOpen = useGeoMarkupStore((state) => state.notes.formIsOpen);
  const isSelectingNoteLocation = useGeoMarkupStore(
    (state) => state.ui.isSelectingNoteLocation
  );
  const isAddNoteDisabled = isNoteFormOpen || isSelectingNoteLocation;

  // Workspace OR filter change → reload list (single effect avoids double-fire)
  useEffect(() => {
    if (currentWorkspaceId) {
      fetchNotes();
    }
  }, [currentWorkspaceId, searchValue, filterKey, fetchNotes]);

  const handleNewNote = useCallback(() => {
    openArtifact('notes', 'new');
  }, [openArtifact]);

  const handleNoteClick = useCallback(
    (noteId: string) => {
      const currentNotes = useGeoMarkupStore.getState().notes.items;
      const note = currentNotes.find(
        (item) => item.id === noteId || item.shortId === noteId
      );
      const coordinates =
        note && note.latitude !== null && note.longitude !== null
          ? { lat: note.latitude, lng: note.longitude }
          : undefined;
      navigateToNoteFromList(note?.shortId ?? noteId, coordinates);
    },
    [navigateToNoteFromList]
  );

  return (
    <ArtifactListPanelContent
      onClose={onClose}
      search={{
        placeholder: t('notes.panel.search'),
        value: searchValue,
        onChange: setSearch,
      }}
      filters={{
        hasActiveFilters,
        label: t('notes.filters.visibility.all'),
        pills: <NoteFilterPills />,
        panel: <NoteFilters />,
      }}
      header={{
        title: t('notes.page.title'),
        count: totalCount,
        addLabel: t('notes.panel.newNote'),
        onAdd: handleNewNote,
        addDisabled: isAddNoteDisabled,
      }}
    >
      <div className={styles.list}>
        <PaginatedList<NoteListItem>
          data={notes}
          renderItem={(note) => (
            <NoteCard
              note={note}
              onClick={handleNoteClick}
              isSelected={note.id === selectedId}
            />
          )}
          keyExtractor={(note) => note.id}
          hasNextPage={hasNextPage}
          onLoadMore={loadMoreNotes}
          isLoading={isLoading}
          hasLoaded={hasLoaded}
          stateRenderers={{
            empty: () => (
              <EmptyState
                icon={<IconNotes size={ICON_SIZE_EMPTY_STATE} stroke={1.5} />}
                title={t('notes.empty.title')}
                description={t('notes.empty.description')}
              />
            ),
          }}
          styling={{ gap: 'xs' }}
          aria-label={t('notes.page.title')}
        />
      </div>
    </ArtifactListPanelContent>
  );
});
